import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, useScroll, useSpring } from 'framer-motion'

// Elementos que se revelan al hacer scroll. Los hijos de grids se revelan como tarjeta completa.
const CANDIDATES = [
  'h1', 'h2', 'h3', 'h4', 'p', 'img',
  '[class*="grid-cols"] > *',
  'dl > div', 'ul > li',
].join(',')

// Nunca se animan: navegación, formularios, carruseles, cosas ya animadas o decorativas.
const SKIP_SELF = 'nav, header, footer, form, button, svg, video, [data-no-reveal], [class*="animate-"], [aria-hidden="true"]'
const SKIP_ANCESTOR = 'nav, header, footer, form, .swiper, [class*="carousel"], [class*="marquee"], [class*="animate-"], [class*="overflow-x"], [data-no-reveal], [role="dialog"]'

// Rutas con animaciones propias.
const EXCLUDED_ROUTES = ['/consultoria']

function prepare(root) {
  const nodes = root.querySelectorAll(CANDIDATES)
  const siblingCount = new Map()
  const fresh = []

  nodes.forEach((el) => {
    if (el.hasAttribute('data-rv')) return
    if (el.matches(SKIP_SELF) || el.closest(SKIP_ANCESTOR)) return
    // si un ancestro ya se revela, este aparece junto con él
    if (el.parentElement?.closest('[data-rv]')) return
    const pos = getComputedStyle(el).position
    if (pos === 'fixed' || pos === 'absolute') return

    const parent = el.parentElement
    const index = siblingCount.get(parent) || 0
    siblingCount.set(parent, index + 1)
    el.style.setProperty('--rv-d', `${Math.min(index, 6) * 90}ms`)

    // grids de 2 columnas: entran desde los lados
    if (parent.className?.toString().includes('lg:grid-cols-2') && parent.children.length === 2) {
      el.setAttribute('data-rv-dir', parent.children[0] === el ? 'left' : 'right')
    }
    el.setAttribute('data-rv', 'pre')
    fresh.push(el)
  })
  return fresh
}

function floatBlobs(root) {
  // si React reutiliza un nodo que ya no es una mancha, se le quita el movimiento
  root.querySelectorAll('[data-float]').forEach((el) => {
    if (!el.matches('[class*="blur-3xl"][aria-hidden="true"], [class*="blur-2xl"][aria-hidden="true"]') || el.hasAttribute('data-no-float')) {
      el.removeAttribute('data-float')
      el.style.animationDelay = ''
    }
  })
  root.querySelectorAll('[class*="blur-3xl"][aria-hidden="true"]:not([data-no-float]), [class*="blur-2xl"][aria-hidden="true"]:not([data-no-float])').forEach((el, i) => {
    if (el.hasAttribute('data-float')) return
    el.setAttribute('data-float', '')
    el.style.animationDelay = `${(i % 5) * -2}s`
  })
}

// Matriz de puntos en un solo lado (alterna izquierda/derecha) en secciones de fondo blanco.
function dotSections() {
  const isWhite = (el) => {
    const cs = getComputedStyle(el)
    const bg = cs.backgroundColor
    const plain = bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent' || bg === 'rgb(255, 255, 255)'
    return plain && cs.backgroundImage === 'none'
  }
  let n = 0
  document.querySelectorAll('#root > div > div').forEach((page) => {
    if (getComputedStyle(page).position === 'fixed') return
    ;[...page.children].forEach((sec) => {
      if (sec.tagName !== 'DIV' || sec.hasAttribute('data-dots') || sec.hasAttribute('data-no-dots')) return
      if (sec.offsetHeight < 320 || !isWhite(sec)) return
      sec.setAttribute('data-dots', n++ % 2 === 0 ? 'right' : 'left')
    })
  })
}

export default function ScrollEffects() {
  const { pathname } = useLocation()
  const { scrollYProgress } = useScroll()
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28, restDelta: 0.001 })

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (!EXCLUDED_ROUTES.includes(pathname)) dotSections()
    if (reduced || EXCLUDED_ROUTES.includes(pathname) || !('IntersectionObserver' in window)) return undefined

    const root = document.getElementById('root')
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const el = entry.target
          io.unobserve(el)
          el.setAttribute('data-rv', 'in')
          const delay = parseInt(el.style.getPropertyValue('--rv-d'), 10) || 0
          // al terminar se libera el elemento para que sus hover/transiciones propias funcionen
          setTimeout(() => el.setAttribute('data-rv', 'done'), delay + 1000)
        })
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    )

    const scan = () => {
      prepare(root).forEach((el) => io.observe(el))
      floatBlobs(root)
    }
    scan()

    // contenido que aparece después (filtros, pestañas, cambio de idioma)
    const mo = new MutationObserver(() => { scan(); dotSections() })
    mo.observe(root, { childList: true, subtree: true })

    return () => {
      mo.disconnect()
      io.disconnect()
      // deja todo visible si se sale de la ruta o se desactiva
      root.querySelectorAll('[data-rv]').forEach((el) => el.removeAttribute('data-rv'))
      root.querySelectorAll('[data-dots]').forEach((el) => el.removeAttribute('data-dots'))
    }
  }, [pathname])

  return (
    <motion.div
      aria-hidden="true"
      className="pointer-events-none fixed left-0 right-0 top-0 z-[100] h-[3px] origin-left bg-gradient-to-r from-electric-500 via-electric-300 to-neon-300"
      style={{ scaleX }}
    />
  )
}
